import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Tactile buttons: a 1px edge and a faint shadow, flat colour only.
 *
 *  · `default` — the PRIMARY action: a solid fill in the company's brand
 *    colour (`--action`, set from /branding; blue when none is set) with
 *    white or near-black text, whichever reads better, and a slightly
 *    darker edge. At most one per view, for the action the view exists
 *    for. Lifts 1px on hover.
 *  · `outline` / `secondary` — the everyday button (surface + line edge).
 *  · `ghost` — low-weight actions in toolbars and rows.
 *  · `destructive` — red text on a secondary button; tints red on hover.
 *    For actions that drop calls or remove capacity.
 *  · `danger` — solid red; the confirm button of a danger dialog only.
 *  · `link` — inline text action in `trace`.
 *
 * Sizes: `sm` (28px) in table rows and cards; `default` (36px) elsewhere.
 * Labels are a verb plus an object, sentence case: "Run lookup".
 */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-[550] tracking-[-0.006em] whitespace-nowrap transition-[background-color,box-shadow,transform,color] duration-[120ms] ease-out outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-ink disabled:pointer-events-none disabled:opacity-45 aria-invalid:shadow-[0_0_0_1px_var(--fault)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-action text-on-action shadow-[0_0_0_1px_var(--action-edge),var(--elev-xs)] hover:-translate-y-px hover:bg-action-hover hover:shadow-[0_0_0_1px_var(--action-edge),var(--elev-sm)] active:translate-y-0 active:shadow-[0_0_0_1px_var(--action-edge)] aria-expanded:bg-action-hover data-[popup-open]:bg-action-hover motion-reduce:hover:translate-y-0",
        outline:
          "bg-surface text-ink shadow-[0_0_0_1px_var(--line),var(--elev-xs)] hover:bg-surface-raised active:shadow-[0_0_0_1px_var(--line-strong)] aria-expanded:bg-surface-raised data-[popup-open]:bg-surface-raised",
        secondary:
          "bg-surface text-ink shadow-[0_0_0_1px_var(--line),var(--elev-xs)] hover:bg-surface-raised active:shadow-[0_0_0_1px_var(--line-strong)] aria-expanded:bg-surface-raised data-[popup-open]:bg-surface-raised",
        ghost:
          "bg-transparent text-ink-muted hover:bg-surface-raised hover:text-ink aria-expanded:bg-surface-raised aria-expanded:text-ink data-[popup-open]:bg-surface-raised data-[popup-open]:text-ink",
        destructive:
          "bg-surface text-fault shadow-[0_0_0_1px_var(--line),var(--elev-xs)] hover:bg-fault-soft hover:shadow-[0_0_0_1px_var(--fault-ring)] focus-visible:outline-fault",
        danger:
          "bg-fault text-surface shadow-[0_0_0_1px_var(--fault),var(--elev-xs)] hover:brightness-[1.06] focus-visible:outline-fault",
        link: "h-auto! px-0! text-trace underline-offset-4 shadow-none hover:underline",
      },
      size: {
        default:
          "h-9 px-3.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-6 gap-1 rounded-sm px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1.5 rounded-sm px-2.5 text-[13px] has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-9",
        "icon-xs":
          "size-6 rounded-sm [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-7 rounded-sm",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  kbd,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /** Keyboard hint shown at the end ("↵", "⌘K") — only where the
     *  shortcut really exists. */
    kbd?: string
  }) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {children}
      {kbd && (
        <kbd className="min-w-4 rounded-[4px] px-1 text-center font-mono text-[11px] leading-4 font-normal opacity-60 shadow-[inset_0_0_0_1px_currentColor]">
          {kbd}
        </kbd>
      )}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
